import { useCallback, useEffect, useRef, useState } from 'react'
import { Crop, RotateCcw, X } from 'lucide-react'
import { Button } from '../ui'
import { ARCHITECTURAL_BLUEPRINT_CROP, NO_BLUEPRINT_CROP } from '../../lib/floorPlan/blueprintImage'
import type { FloorBackgroundCrop } from '../../lib/floorPlan/types'

export type FloorPlanCropDialogProps = {
  imageUrl: string
  initialCrop?: FloorBackgroundCrop
  suggestedCrop?: FloorBackgroundCrop
  title?: string
  onApply: (crop: FloorBackgroundCrop) => void
  onCancel: () => void
}

type Edge = 'n' | 's' | 'e' | 'w' | 'move'

function clamp(n: number, lo: number, hi: number) {
  return Math.min(hi, Math.max(lo, n))
}

function normalizeCrop(c: FloorBackgroundCrop): Required<FloorBackgroundCrop> {
  let top = clamp(c.top ?? 0, 0, 0.45)
  let bottom = clamp(c.bottom ?? 0, 0, 0.45)
  let left = clamp(c.left ?? 0, 0, 0.45)
  let right = clamp(c.right ?? 0, 0, 0.45)
  if (top + bottom > 0.85) {
    const s = 0.85 / (top + bottom)
    top *= s
    bottom *= s
  }
  if (left + right > 0.85) {
    const s = 0.85 / (left + right)
    left *= s
    right *= s
  }
  return { top, bottom, left, right }
}

function pctLabel(v: number) {
  return `${Math.round(v * 100)}%`
}

export default function FloorPlanCropDialog({
  imageUrl,
  initialCrop,
  suggestedCrop,
  title = 'Crop floor plan image',
  onApply,
  onCancel,
}: FloorPlanCropDialogProps) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const [crop, setCrop] = useState<Required<FloorBackgroundCrop>>(() =>
    normalizeCrop(initialCrop ?? suggestedCrop ?? NO_BLUEPRINT_CROP),
  )
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 })
  const dragRef = useRef<{
    edge: Edge
    startX: number
    startY: number
    startCrop: Required<FloorBackgroundCrop>
  } | null>(null)

  useEffect(() => {
    const img = new Image()
    img.onload = () => setImgSize({ w: img.naturalWidth, h: img.naturalHeight })
    img.src = imageUrl
  }, [imageUrl])

  const layout = useCallback(() => {
    const el = wrapRef.current
    if (!el || imgSize.w < 1) return null
    const cw = el.clientWidth
    const ch = el.clientHeight
    const scale = Math.min(cw / imgSize.w, ch / imgSize.h)
    const dw = imgSize.w * scale
    const dh = imgSize.h * scale
    const ox = (cw - dw) / 2
    const oy = (ch - dh) / 2
    return { cw, ch, dw, dh, ox, oy }
  }, [imgSize])

  const cropRect = useCallback(
    (c: Required<FloorBackgroundCrop>) => {
      const L = layout()
      if (!L) return null
      const n = normalizeCrop(c)
      return {
        x: L.ox + n.left * L.dw,
        y: L.oy + n.top * L.dh,
        w: L.dw * (1 - n.left - n.right),
        h: L.dh * (1 - n.top - n.bottom),
        ...L,
      }
    },
    [layout],
  )

  const pointerToCrop = useCallback(
    (clientX: number, clientY: number, edge: Edge, startCrop: Required<FloorBackgroundCrop>) => {
      const L = layout()
      const el = wrapRef.current
      if (!L || !el) return startCrop
      const rect = el.getBoundingClientRect()
      const lx = clientX - rect.left
      const ly = clientY - rect.top
      const nx = clamp((lx - L.ox) / L.dw, 0, 1)
      const ny = clamp((ly - L.oy) / L.dh, 0, 1)
      const s = normalizeCrop(startCrop)

      if (edge === 'move') {
        const dx = nx - (dragRef.current?.startX ?? nx)
        const dy = ny - (dragRef.current?.startY ?? ny)
        return normalizeCrop({
          top: s.top + dy,
          bottom: s.bottom - dy,
          left: s.left + dx,
          right: s.right - dx,
        })
      }
      if (edge === 'n') return normalizeCrop({ ...s, top: ny })
      if (edge === 's') return normalizeCrop({ ...s, bottom: 1 - ny })
      if (edge === 'w') return normalizeCrop({ ...s, left: nx })
      if (edge === 'e') return normalizeCrop({ ...s, right: 1 - nx })
      return s
    },
    [layout],
  )

  const hitEdge = useCallback(
    (clientX: number, clientY: number): Edge | null => {
      const R = cropRect(crop)
      const el = wrapRef.current
      if (!R || !el) return null
      const rect = el.getBoundingClientRect()
      const x = clientX - rect.left
      const y = clientY - rect.top
      const tol = 10
      const inX = x >= R.x && x <= R.x + R.w
      const inY = y >= R.y && y <= R.y + R.h
      if (Math.abs(y - R.y) <= tol && inX) return 'n'
      if (Math.abs(y - (R.y + R.h)) <= tol && inX) return 's'
      if (Math.abs(x - R.x) <= tol && inY) return 'w'
      if (Math.abs(x - (R.x + R.w)) <= tol && inY) return 'e'
      if (inX && inY) return 'move'
      return null
    },
    [crop, cropRect],
  )

  useEffect(() => {
    function onMove(e: PointerEvent) {
      const d = dragRef.current
      if (!d) return
      setCrop(pointerToCrop(e.clientX, e.clientY, d.edge, d.startCrop))
    }
    function onUp() {
      dragRef.current = null
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
  }, [pointerToCrop])

  const R = cropRect(crop)
  const n = normalizeCrop(crop)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="flex max-h-[95vh] w-full max-w-3xl flex-col rounded-xl border border-mg-border bg-mg-surface shadow-2xl">
        <div className="flex items-center justify-between border-b border-mg-border px-4 py-3">
          <div className="flex items-center gap-2">
            <Crop size={18} className="text-mg-accent" />
            <div>
              <p className="text-sm font-semibold text-mg-text">{title}</p>
              <p className="text-[11px] text-mg-muted">
                Drag edges or the box to exclude legend, title block, and margins. Bright area = floor.
              </p>
            </div>
          </div>
          <button type="button" className="rounded-md p-1 text-mg-muted hover:bg-mg-page" onClick={onCancel}>
            <X size={18} />
          </button>
        </div>

        <div
          ref={wrapRef}
          className="relative mx-4 mt-3 min-h-[280px] flex-1 cursor-crosshair overflow-hidden rounded-lg bg-neutral-900"
          style={{ maxHeight: '52vh' }}
          onPointerDown={e => {
            const edge = hitEdge(e.clientX, e.clientY)
            if (!edge) return
            dragRef.current = {
              edge,
              startX: 0,
              startY: 0,
              startCrop: { ...crop },
            }
            if (edge === 'move') {
              const L = layout()
              const rect = wrapRef.current!.getBoundingClientRect()
              if (L) {
                dragRef.current.startX = (e.clientX - rect.left - L.ox) / L.dw
                dragRef.current.startY = (e.clientY - rect.top - L.oy) / L.dh
              }
            }
            ;(e.target as HTMLElement).setPointerCapture?.(e.pointerId)
          }}
        >
          {R && (
            <>
              <img
                src={imageUrl}
                alt="Crop preview"
                className="pointer-events-none absolute select-none"
                style={{ left: R.ox, top: R.oy, width: R.dw, height: R.dh }}
                draggable={false}
              />
              <div className="pointer-events-none absolute bg-black/55" style={{ left: 0, top: 0, width: R.cw, height: R.y }} />
              <div
                className="pointer-events-none absolute bg-black/55"
                style={{ left: 0, top: R.y + R.h, width: R.cw, height: Math.max(0, R.ch - R.y - R.h) }}
              />
              <div className="pointer-events-none absolute bg-black/55" style={{ left: 0, top: R.y, width: R.x, height: R.h }} />
              <div
                className="pointer-events-none absolute bg-black/55"
                style={{ left: R.x + R.w, top: R.y, width: Math.max(0, R.cw - R.x - R.w), height: R.h }}
              />
              <div
                className="pointer-events-none absolute border-2 border-sky-400 shadow-[0_0_0_1px_rgba(56,189,248,0.35)]"
                style={{ left: R.x, top: R.y, width: R.w, height: R.h }}
              />
            </>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3 px-4 py-3 sm:grid-cols-4">
          {(
            [
              ['Top', 'top'],
              ['Bottom', 'bottom'],
              ['Left', 'left'],
              ['Right', 'right'],
            ] as const
          ).map(([label, key]) => (
            <label key={key} className="text-[11px] text-mg-muted">
              {label} · {pctLabel(n[key])}
              <input
                type="range"
                min={0}
                max={40}
                step={0.5}
                value={n[key] * 100}
                className="floor-tx-slider mt-1 w-full"
                onChange={e =>
                  setCrop(c => normalizeCrop({ ...c, [key]: Number(e.target.value) / 100 }))
                }
              />
            </label>
          ))}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-mg-border px-4 py-3">
          <div className="flex flex-wrap gap-2">
            <Button
              variant="ghost"
              size="sm"
              type="button"
              onClick={() => setCrop(normalizeCrop(ARCHITECTURAL_BLUEPRINT_CROP))}
            >
              Legend preset
            </Button>
            <Button variant="ghost" size="sm" type="button" onClick={() => setCrop({ top: 0, bottom: 0, left: 0, right: 0 })}>
              <RotateCcw size={14} /> No crop
            </Button>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" type="button" onClick={onCancel}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" type="button" onClick={() => onApply(normalizeCrop(crop))}>
              Apply crop
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
